"use client";

import { FormProvider, useWvcForm } from "@/integrations/forms/FormProvider";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Separator } from "@/components/ui/separator";
import { Card, CardContent } from "@/components/ui/card";
import { Form, FormField, FormItem, FormLabel, FormControl, FormMessage } from "@/components/ui/form";
import { Link } from "@/components/common/Link";
import { MapPin, Phone, Mail, Clock, Loader } from "lucide-react";
export default function GetInTouch() {
  return <section className="relative bg-gradient-to-br from-background via-background to-muted-light/30 py-20 px-4 md:px-6 lg:px-8 overflow-hidden">
      <div className="absolute inset-0 bg-[radial-gradient(circle_at_30%_50%,hsl(220,80%,98%)_0%,transparent_50%)] pointer-events-none" />
      <div className="absolute inset-0 bg-[radial-gradient(circle_at_70%_80%,hsl(170,75%,98%)_0%,transparent_50%)] pointer-events-none" />

      <div className="relative max-w-7xl mx-auto">
        <div className="text-center mb-16">
          <h2 className="font-display text-4xl md:text-5xl lg:text-6xl font-bold text-foreground mb-5 tracking-tight">
            Get In Touch
          </h2>
          <p className="text-muted text-lg md:text-xl max-w-3xl mx-auto leading-relaxed">
            Have questions or need a quote? Reach out to AA Eagle Plumbing and our team will get back to you promptly. We are a mobile service but we are based in Miami, Florida.
          </p>
        </div>

        <div className="grid lg:grid-cols-2 gap-10 items-start">
          <FormProvider className="w-full" formId="contact-form-get-in-touch" sectionName="get-in-touch" defaultValues={{
          name: "",
          email: "",
          phone: "",
          message: ""
        }} validationRules={{
          name: {
            required: true,
            minLength: 2,
            maxLength: 50
          },
          email: {
            required: true,
            pattern: "email"
          },
          phone: {
            required: false,
            pattern: "phone"
          },
          message: {
            required: true,
            minLength: 10,
            maxLength: 500
          }
        }} submitText="Send Message" successMessage="Thank you! We'll get back to you within 24 hours.">
            <ContactForm />
          </FormProvider>

          <Card className="bg-white/80 backdrop-blur-sm border-border/60 shadow-medium hover:shadow-strong transition-all duration-500 ease-[var(--ease-out-back)] hover:-translate-y-1">
            <CardContent className="space-y-7">
              <div className="flex items-start gap-5">
                <div className="flex-shrink-0 w-12 h-12 rounded-xl bg-gradient-to-br from-primary/20 to-primary/10 flex items-center justify-center shadow-sm">
                  <MapPin className="size-6 text-primary" />
                </div>
                <div>
                  <h3 className="font-display font-bold text-lg text-foreground mb-2">Address</h3>
                  <p className="text-muted text-sm leading-relaxed">Based in Miami, Florida</p>
                  <p className="text-muted text-sm">Mobile service available</p>
                </div>
              </div>

              <Separator className="bg-border" />

              <div className="flex items-start gap-5">
                <div className="flex-shrink-0 w-12 h-12 rounded-xl bg-gradient-to-br from-secondary/20 to-secondary/10 flex items-center justify-center shadow-sm">
                  <Phone className="size-6 text-secondary-dark" />
                </div>
                <div>
                  <h3 className="font-display font-bold text-lg text-foreground mb-2">Phone</h3>
                  <p className="text-muted text-sm">954-802-3664</p>
                </div>
              </div>

              <Separator className="bg-border" />

              <div className="flex items-start gap-5">
                <div className="flex-shrink-0 w-12 h-12 rounded-xl bg-gradient-to-br from-accent/20 to-accent/10 flex items-center justify-center shadow-sm">
                  <Mail className="size-6 text-accent-dark" />
                </div>
                <div>
                  <h3 className="font-display font-bold text-lg text-foreground mb-2">Email</h3>
                  <p className="text-muted text-sm">
                    <Link to="mailto:floridapumpssolution@yahoo.com" className="text-primary hover:underline transition-all duration-200">
                      floridapumpssolution@yahoo.com
                    </Link>
                  </p>
                </div>
              </div>

              <Separator className="bg-border" />

              <div className="flex items-start gap-5">
                <div className="flex-shrink-0 w-12 h-12 rounded-xl bg-gradient-to-br from-primary/20 to-primary/10 flex items-center justify-center shadow-sm">
                  <Clock className="size-6 text-primary" />
                </div>
                <div>
                  <h3 className="font-display font-bold text-lg text-foreground mb-2">Business Hours</h3>
                  <p className="text-muted text-sm">9 am until 5 pm</p>
                  <p className="text-muted text-sm">Emergency services available</p>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    </section>;
}
function ContactForm() {
  const {
    isSubmitting,
    isSubmitted,
    submitError,
    successMessage,
    handleSubmit,
    resetForm,
    control
  } = useWvcForm();
  if (isSubmitted && !submitError) {
    return <Card className="bg-white/80 backdrop-blur-sm border-border/60 shadow-medium">
        <CardContent className="text-center py-16">
          <div className="w-20 h-20 rounded-full bg-gradient-to-br from-primary/20 to-primary/10 flex items-center justify-center mx-auto mb-6 shadow-soft">
            <Mail className="size-10 text-primary" />
          </div>
          <h3 className="font-display text-3xl font-bold text-foreground mb-3">
            Message Sent!
          </h3>
          <p className="text-muted text-lg mb-8">{successMessage}</p>
          <Button onClick={resetForm} variant="outline" size="lg" className="hover:-translate-y-0.5 transition-all duration-200 shadow-soft">
            Send Another Message
          </Button>
        </CardContent>
      </Card>;
  }
  return <Card className="bg-white/80 backdrop-blur-sm border-border/60 shadow-medium hover:shadow-strong transition-all duration-500 ease-[var(--ease-out-back)] hover:-translate-y-1">
      <CardContent>
        <Form onSubmit={handleSubmit}>
          <div className="space-y-6">
            <FormField control={control} name="name" rules={{
            required: "Name is required",
            minLength: {
              value: 2,
              message: "Name must be at least 2 characters"
            },
            maxLength: {
              value: 50,
              message: "Name must be less than 50 characters"
            }
          }} render={({
            field
          }) => <FormItem>
                  <FormLabel className="text-foreground font-semibold text-sm tracking-wide">Full Name</FormLabel>
                  <FormControl>
                    <Input placeholder="John Doe" className="h-11 bg-background/50 border-border/60 focus-visible:border-primary focus-visible:shadow-sm transition-all duration-200" {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>} />

            <FormField control={control} name="email" rules={{
            required: "Email is required",
            pattern: {
              value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
              message: "Please enter a valid email address"
            }
          }} render={({
            field
          }) => <FormItem>
                  <FormLabel className="text-foreground font-semibold text-sm tracking-wide">Email Address</FormLabel>
                  <FormControl>
                    <Input type="email" placeholder="you@example.com" className="h-11 bg-background/50 border-border/60 focus-visible:border-primary focus-visible:shadow-sm transition-all duration-200" {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>} />

            <FormField control={control} name="phone" rules={{
            pattern: {
              value: /^[\d\s\-\+\(\)]+$/,
              message: "Please enter a valid phone number"
            }
          }} render={({
            field
          }) => <FormItem>
                  <FormLabel className="text-foreground font-semibold text-sm tracking-wide">Phone (Optional)</FormLabel>
                  <FormControl>
                    <Input type="tel" placeholder="(555) 555-1234" className="h-11 bg-background/50 border-border/60 focus-visible:border-primary focus-visible:shadow-sm transition-all duration-200" {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>} />

            <FormField control={control} name="message" rules={{
            required: "Message is required",
            minLength: {
              value: 10,
              message: "Message must be at least 10 characters"
            },
            maxLength: {
              value: 500,
              message: "Message must be less than 500 characters"
            }
          }} render={({
            field
          }) => <FormItem>
                  <FormLabel className="text-foreground font-semibold text-sm tracking-wide">Message</FormLabel>
                  <FormControl>
                    <Textarea placeholder="Tell us about your plumbing needs..." className="bg-background/50 border-border/60 focus-visible:border-primary focus-visible:shadow-sm transition-all duration-200 min-h-36" {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>} />

            {submitError && <div className="p-5 bg-destructive/10 border border-destructive/30 rounded-lg">
                <p className="text-destructive text-sm font-medium">{submitError}</p>
              </div>}

            <Button type="submit" disabled={isSubmitting} size="lg" className="w-full bg-accent-dark hover:bg-accent-dark text-foreground-accent font-semibold shadow-medium hover:shadow-strong hover:-translate-y-0.5 transition-all duration-300 ease-[var(--ease-spring)] h-12">
              {isSubmitting ? <>
                  <Loader className="animate-spin size-5" />
                  Sending...
                </> : 'Send Message'}
            </Button>
          </div>
        </Form>
      </CardContent>
    </Card>;
}